<template>
  <div class="resident-wrapper">
    <!-- Modal de Vinculação -->
    <b-modal
      no-close-on-backdrop
      hide-footer
      id="linkResident"
      title="VINCULAR RESIDENTE"
      :size="isNotCustomerHasResident.length == 0 ? 'md' : 'lg'"
      modal-class="resident-modal"
    >
      <!-- Estado vazio -->
      <div class="empty-state" v-if="isNotCustomerHasResident.length == 0">
        <i class="fas fa-user-slash empty-state-icon"></i>
        <h6 class="empty-state-text">
          Sem residentes disponíveis para vinculação
        </h6>
        <p class="empty-state-description">
          Todos os residentes já estão vinculados a este familiar
        </p>
      </div>

      <!-- Lista de residentes disponíveis -->
      <div v-if="isNotCustomerHasResident.length > 0">
        <h6 class="search-label">PESQUISAR RESIDENTE:</h6>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Digite o nome do residente"
          single-line
          hide-details
          outlined
          dense
          class="mb-4 search-field"
        />

        <v-data-table
          dense
          :headers="[
            { text: 'NOME', value: 'name' },
            { text: 'GÊNERO', value: 'gender_name' },
            { text: 'IDADE', value: 'date_of_birth' },
          ]"
          :items="isNotCustomerHasResident"
          :search="search"
          :footer-props="{
            'items-per-page-options': [10, 25, 100],
            'items-per-page-text': 'Mostrar',
          }"
          @click:row="createCustomerHasResident"
          class="resident-table"
        >
          <template v-slot:[`item.date_of_birth`]="{ item }">
            <td>
              <span
                v-b-tooltip.html
                :title="`Data de nascimento <br /> ${formatDate(
                  item.date_of_birth,
                  false
                )}`"
                class="age-badge"
              >
                {{ getAge(item.date_of_birth) }} anos
              </span>
            </td>
          </template>
        </v-data-table>
      </div>
    </b-modal>

    <!-- Lista de Residentes Vinculados -->
    <b-overlay :show="loadingCustomerHasResident" opacity="0.99">
      <div class="resident-header">
        <div class="header-content">
          <div class="header-title">
            <i class="fas fa-link header-icon"></i>
            <h6 class="title-pages mb-0">RESIDENTES VINCULADOS</h6>
          </div>
          <v-btn
            class="btn-add"
            @click="$bvModal.show('linkResident')"
            :disabled="loadingCustomerHasResident"
          >
            <i class="fas fa-plus mr-2"></i>
            <span>VINCULAR</span>
          </v-btn>
        </div>
      </div>

      <!-- Estado vazio -->
      <div class="empty-state-list" v-show="isCustomerHasResident.length == 0">
        <i class="fas fa-users empty-icon"></i>
        <h6 class="empty-text">Nenhum residente vinculado</h6>
        <p class="empty-description">
          Clique em "VINCULAR" para adicionar residentes
        </p>
      </div>

      <div class="mb-2">
        <ErrorsMessages :errors="errors" />
      </div>

      <!-- Lista de residentes -->
      <div class="residents-list" v-show="isCustomerHasResident.length > 0">
        <div
          v-for="(item, key) in isCustomerHasResident"
          :key="key"
          class="resident-card"
        >
          <div class="resident-info-section">
            <div class="resident-details">
              <div class="info-item">
                <label class="info-label">Nome:</label>
                <span class="info-value">{{ item.resident_name }}</span>
              </div>

              <div class="info-item">
                <label class="info-label">Data de Nascimento:</label>
                <span class="info-value">
                  {{ formatDate(item.date_of_birth, false) }} -
                  {{ getAge(item.date_of_birth) }} anos
                </span>
              </div>

              <div class="info-item">
                <label class="info-label">Gênero:</label>
                <span class="info-value">{{ item.gender_name }}</span>
              </div>
            </div>

            <div class="resident-actions">
              <div class="checkbox-section">
                <div class="custom-checkbox-wrapper">
                  <input
                    :id="`responsible_for_payment_${item.resident_id}`"
                    type="checkbox"
                    :checked="item.responsible_for_payment"
                    :ref="`responsible_for_payment_${item.resident_id}`"
                    @click="handleChangeResponsibleForPayment(item)"
                    class="custom-checkbox"
                  />
                  <label
                    :for="`responsible_for_payment_${item.resident_id}`"
                    class="checkbox-label"
                  >
                    <i class="fas fa-money-bill-wave mr-2"></i>
                    Responsável pelo pagamento
                  </label>
                </div>

                <div class="custom-checkbox-wrapper">
                  <input
                    :id="`responsible_for_contract_${item.resident_id}`"
                    type="checkbox"
                    :checked="item.responsible_for_contract"
                    :ref="`responsible_for_contract_${item.resident_id}`"
                    @click="handleChangeResponsibleForContract(item)"
                    class="custom-checkbox"
                  />
                  <label
                    :for="`responsible_for_contract_${item.resident_id}`"
                    class="checkbox-label"
                  >
                    <i class="fas fa-file-contract mr-2"></i>
                    Responsável pelo contrato
                  </label>
                </div>
              </div>

              <div class="delete-action">
                <ConfirmationDialog
                  :disabled="loadingCustomerHasResident"
                  :loading="loadingCustomerHasResident"
                  headerClass="modal-header-red"
                  message-title="REMOVER VÍNCULO?"
                  message-body="Deseja realmente remover o vínculo deste residente com o familiar?"
                  open-class="btn-delete"
                  open-left-icon="fa-times"
                  openText="EXCLUIR"
                  @yes="deleteCustomerHasResident(item.resident_id)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import ConfirmationDialog from "../Components/ConfirmationDialog";
import ErrorsMessages from "../Components/ErrorsMessages";
import api from "../../services/api";
import toast from "../../lib/ToastNotification";
import { formatDate } from "../../lib/format";
import { getAge } from "../../lib/getAgeByDate";

export default {
  name: "CustomerHasResident",

  components: {
    ConfirmationDialog,
    ErrorsMessages,
  },

  props: {
    customer_id: [String, Number],
  },

  data() {
    return {
      errors: [],
      search: "",
      residents: [],
      loadingCustomerHasResident: false,
      isCustomerHasResident: [],
      isNotCustomerHasResident: [],
    };
  },

  created() {
    this.retrieve();
  },

  methods: {
    getAge,
    formatDate,

    async retrieve() {
      if (this.errors.length == 0) await this.getResidentHasCustomer();
      if (this.errors.length == 0) await this.getNotCustomerHasResident();
    },

    async getResidentHasCustomer() {
      try {
        this.loadingCustomerHasResident = true;

        const { customer_id } = this;

        const response = await api.get("/rest-home/residents/by-customer", {
          params: { customer_id },
        });

        const { error, errors, customerHasResident } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.isCustomerHasResident = customerHasResident;
        }

        this.loadingCustomerHasResident = false;
      } catch (error) {
        console.log(error);
      }
    },

    async getNotCustomerHasResident() {
      try {
        const { customer_id } = this;

        let params = { customer_id };

        const response = await api.get(
          "/rest-home/residents/unrelated-residents",
          {
            params,
          }
        );

        const { error, errors, residents } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.isNotCustomerHasResident = residents;
        }
      } catch (error) {
        console.log(error);
      }
    },

    async createCustomerHasResident({ resident_id }) {
      try {
        this.loadingCustomerHasResident = true;

        const { customer_id } = this;

        let params = { customer_id, resident_id };

        const response = await api.post(
          "/rest-home/customer/bind-resident",
          params
        );

        const { error, message } = response.data;

        if (!error) {
          toast(message, "success");
          this.getResidentHasCustomer();
          this.getNotCustomerHasResident();
          this.$bvModal.hide("linkResident");
        } else {
          toast(message, "error");
        }

        this.loadingCustomerHasResident = false;
      } catch (error) {
        console.log(error);
      }
    },

    async deleteCustomerHasResident(resident_id) {
      try {
        this.loadingCustomerHasResident = true;

        const { customer_id } = this;

        let data = { customer_id, resident_id };

        const response = await api.delete(
          "/rest-home/customer/unbind-resident",
          {
            data,
          }
        );

        const { error, message } = response.data;

        if (!error) {
          toast(message, "success");
          this.getResidentHasCustomer();
          this.getNotCustomerHasResident();
        } else {
          toast(message, "error");
        }

        this.loadingCustomerHasResident = false;
      } catch (error) {
        console.log(error);
      }
    },

    async handleChangeResponsibleForPayment(item) {
      try {
        const { customer_id } = this;
        const { resident_id } = item;

        var responsible_for_payment =
          this.$refs[`responsible_for_payment_${resident_id}`][0].checked;

        let params = { resident_id, customer_id, responsible_for_payment };

        const response = await api.post(
          "/rest-home/customer/responsible-for-payment/update",
          params
        );

        const { error, errors, message } = response.data;

        if (!error) {
          toast(message, "success");
          this.getResidentHasCustomer();
          this.errors = [];
        } else {
          this.errors = errors;
        }
      } catch (error) {
        console.log(error);
      }
    },

    async handleChangeResponsibleForContract(item) {
      try {
        const { customer_id } = this;
        const { resident_id } = item;

        let responsible_for_contract =
          this.$refs[`responsible_for_contract_${item.resident_id}`][0].checked;

        let params = { resident_id, customer_id, responsible_for_contract };

        const response = await api.post(
          "/rest-home/customer/responsible-for-contract/update",
          params
        );

        const { error, errors, message } = response.data;

        if (!error) {
          toast(message, "success");
          this.getResidentHasCustomer();
          this.errors = [];
        } else {
          this.errors = errors;
        }
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
/* Container Principal */
.resident-wrapper {
  padding: 1rem 0;
}

/* Header - Usando padrões do sistema */
.resident-header {
  background: #f9f9f9;
  border: 1px solid #ddd;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-icon {
  color: #30915b;
  font-size: 1.25rem;
}

/* Botão Adicionar - Seguindo padrão do sistema */
.btn-add {
  background-color: #33495e !important;
  color: #fff !important;
  font-family: WorkSans-Regular !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  font-size: 13px !important;
  padding: 0.5rem 1.5rem !important;
  height: auto !important;
  box-shadow: none !important;
  transition: all 0.2s ease;
}

.btn-add:hover:not(:disabled) {
  background-color: #1f5a76 !important;
  opacity: 0.9;
}

.btn-add:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Modal */
.resident-modal >>> .modal-header {
  background-color: #33495e;
  color: #fff;
  border-bottom: none;
}

.resident-modal >>> .modal-title {
  font-family: WorkSans-SemiBold !important;
  font-size: 16px;
  letter-spacing: 0.5px;
}

.search-label {
  font-family: WorkSans-SemiBold !important;
  color: #33495e;
  font-size: 13px;
  margin-bottom: 0.75rem;
  letter-spacing: 0.5px;
}

.search-field {
  background-color: #f8f9fa;
}

.search-field >>> .v-input__slot {
  font-family: WorkSans-Regular !important;
  font-size: 14px !important;
}

/* Tabela - Usando padrões do sistema */
.resident-table {
  overflow: hidden;
}

.resident-table >>> .v-data-table__wrapper {
  border: 1px solid #ddd;
}

.resident-table >>> thead th {
  background-color: #eee !important;
  color: #444 !important;
  font-family: WorkSans-SemiBold !important;
  font-size: 13px !important;
  text-transform: uppercase;
  border-bottom: 1px solid #ddd !important;
}

.resident-table >>> tbody tr {
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-family: WorkSans-Regular !important;
}

.resident-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.resident-table >>> tbody td {
  font-size: 14px !important;
  color: #444;
  border-bottom: 1px solid #ddd !important;
}

.age-badge {
  display: inline-block;
  padding: 4px 12px;
  background-color: #e3f2fd;
  color: #1d75be;
  font-size: 13px;
  font-weight: 500;
  font-family: WorkSans-Regular !important;
}

/* Estado Vazio - Modal */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
}

.empty-state-icon {
  font-size: 4rem;
  color: #cbd5e0;
  margin-bottom: 1rem;
}

.empty-state-text {
  font-family: WorkSans-SemiBold !important;
  color: #33495e;
  font-size: 16px;
  margin-bottom: 0.5rem;
}

.empty-state-description {
  font-family: WorkSans-Regular !important;
  color: #666;
  font-size: 14px;
  margin: 0;
}

/* Estado Vazio - Lista */
.empty-state-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: #f9f9f9;
  border: 2px dashed #ddd;
}

.empty-icon {
  font-size: 5rem;
  color: #cbd5e0;
  margin-bottom: 1.5rem;
}

.empty-text {
  font-family: WorkSans-SemiBold !important;
  color: #33495e;
  font-size: 18px;
  margin-bottom: 0.5rem;
}

.empty-description {
  font-family: WorkSans-Regular !important;
  color: #666;
  font-size: 15px;
  margin: 0;
}

/* Lista de Residentes */
.residents-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.resident-card {
  background: #fff;
  border: 1px solid #ddd;
  padding: 1.5rem;
  transition: all 0.2s ease;
}

.resident-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #bbb;
}

.resident-info-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
  flex-wrap: wrap;
}

.resident-details {
  flex: 1;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.info-item {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.info-label {
  font-family: WorkSans-SemiBold !important;
  color: #33495e;
  font-size: 14px;
  min-width: 150px;
  margin: 0;
}

.info-value {
  font-family: WorkSans-Regular !important;
  color: #444;
  font-size: 14px;
  word-break: break-word;
}

/* Ações */
.resident-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-end;
}

.checkbox-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.custom-checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #f8f9fa;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  transition: all 0.2s ease;
}

.custom-checkbox-wrapper:hover {
  background-color: #e9ecef;
  border-color: #33495e;
}

.custom-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin: 0;
}

.custom-checkbox:checked {
  accent-color: #33495e;
}

.checkbox-label {
  font-family: WorkSans-Regular !important;
  color: #444;
  font-size: 13px;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  user-select: none;
}

.checkbox-label i {
  color: #30915b;
  font-size: 14px;
}

.delete-action {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

/* Botão Excluir */
.delete-action >>> .btn-delete {
  background-color: #dc3545 !important;
  color: #fff !important;
  font-family: WorkSans-Regular !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  text-transform: uppercase;
  padding: 0.5rem 1.25rem !important;
  box-shadow: none !important;
  transition: all 0.2s ease;
}

.delete-action >>> .btn-delete:hover:not(:disabled) {
  background-color: #c0392b !important;
  opacity: 0.9;
}

/* Responsividade */
@media (max-width: 992px) {
  .checkbox-section {
    width: 100%;
  }

  .custom-checkbox-wrapper {
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .header-title {
    justify-content: center;
  }

  .btn-add {
    width: 100%;
    justify-content: center;
  }

  .resident-info-section {
    flex-direction: column;
    gap: 1.5rem;
  }

  .resident-details {
    min-width: 100%;
  }

  .resident-actions {
    width: 100%;
    align-items: stretch;
  }

  .checkbox-section {
    width: 100%;
  }

  .custom-checkbox-wrapper {
    width: 100%;
    justify-content: flex-start;
  }

  .delete-action {
    justify-content: stretch;
  }

  .delete-action >>> .btn-delete {
    width: 100%;
  }

  .info-item {
    flex-direction: column;
    gap: 0.25rem;
  }

  .info-label {
    min-width: auto;
  }
}

@media (max-width: 576px) {
  .resident-wrapper {
    padding: 0.5rem 0;
  }

  .resident-header {
    padding: 1rem;
  }

  .resident-card {
    padding: 1rem;
  }

  .header-title h6 {
    font-size: 14px;
  }

  .header-icon {
    font-size: 1rem;
  }

  .empty-state-icon,
  .empty-icon {
    font-size: 3rem;
  }

  .empty-state,
  .empty-state-list {
    padding: 2rem 1rem;
  }

  .info-label,
  .info-value {
    font-size: 13px;
  }

  .checkbox-label {
    font-size: 12px;
  }
}

/* Acessibilidade */
@media (prefers-reduced-motion: reduce) {
  .resident-card,
  .resident-table >>> tbody tr,
  .custom-checkbox-wrapper,
  .btn-add,
  .delete-action >>> .btn-delete {
    transition: none;
  }
}

/* Modo de impressão */
@media print {
  .resident-header,
  .btn-add,
  .delete-action,
  .custom-checkbox {
    display: none;
  }

  .resident-card {
    page-break-inside: avoid;
    box-shadow: none;
    border: 1px solid #000;
  }

  .checkbox-label {
    color: #000;
  }
}

/* Ajustes para overlay de loading */
.resident-wrapper >>> .b-overlay-wrap {
  min-height: 200px;
}
</style>